import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ExternalLink } from 'lucide-react';

const BrandShowcase = () => {
  const { t, language } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [hoveredLogo, setHoveredLogo] = useState(null);

  const clientLogos = [
    { 
      name: "LA MAISON", 
      bg: "bg-black", 
      textColor: "text-amber-400", 
      style: "font-serif italic text-xl",
      description: language === 'fr' ? 'Restaurant gastronomique' : 'Fine dining restaurant'
    },
    { 
      name: "Gala", 
      bg: "bg-red-600", 
      textColor: "text-white", 
      style: "font-serif font-bold text-2xl",
      description: language === 'fr' ? 'Événementiel' : 'Events'
    },
    { 
      name: "BAO BRUNCH", 
      bg: "bg-white border-2 border-neutral-200", 
      textColor: "text-black", 
      style: "font-sans tracking-widest text-sm font-bold",
      description: language === 'fr' ? 'Brunch & café' : 'Brunch & coffee'
    },
    { 
      name: "LUXE", 
      bg: "bg-blue-900", 
      textColor: "text-white", 
      style: "font-serif tracking-wider text-xl",
      description: language === 'fr' ? 'Mode de luxe' : 'Luxury fashion'
    },
    { 
      name: "AZAR", 
      bg: "bg-red-700", 
      textColor: "text-white", 
      style: "font-sans font-bold text-xl tracking-wide",
      description: language === 'fr' ? 'Club & nightlife' : 'Club & nightlife'
    },
    { 
      name: "TESLA", 
      bg: "bg-white border-2 border-neutral-200", 
      textColor: "text-black", 
      style: "font-sans font-medium tracking-[0.3em] text-lg",
      description: language === 'fr' ? 'Automobile électrique' : 'Electric vehicles'
    },
  ];

  return (
    <section 
      ref={ref}
      className="py-24 md:py-32 bg-neutral-50"
      data-testid="brand-showcase-section"
    >
      {/* Full width image banner */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ duration: 1 }}
        className="w-full mb-16 md:mb-24"
      >
        <img
          src="https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=1600&q=80"
          alt="Brand showcase"
          className="w-full h-[40vh] md:h-[50vh] object-cover"
        />
      </motion.div>

      <div className="container-custom">
        {/* Headlines */}
        <div className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-block text-xs font-semibold tracking-[0.3em] uppercase text-neutral-500 mb-6"
          >
            {language === 'fr' ? 'Ils nous font confiance' : 'They trust us'}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="heading-xl text-4xl md:text-5xl lg:text-6xl mb-4 text-black"
            data-testid="brand-title-1"
          >
            <span className="underline-animation active">{t('brand.line1')}</span>
          </motion.h2>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="heading-xl text-4xl md:text-5xl lg:text-6xl text-black"
            data-testid="brand-title-2"
          >
            <span className="underline-animation active">{t('brand.line2')}</span>
          </motion.h2>
        </div>

        {/* Client Logo Grid */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto mb-16"
          data-testid="client-logos-grid"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.1, delayChildren: 0.3 }
            }
          }}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {clientLogos.map((logo, index) => (
            <motion.div
              key={logo.name}
              variants={{
                hidden: { opacity: 0, scale: 0.8, rotateY: -30 },
                visible: { 
                  opacity: 1, 
                  scale: 1, 
                  rotateY: 0,
                  transition: {
                    type: "spring",
                    stiffness: 100,
                    damping: 12,
                  }
                }
              }}
              whileHover={{ 
                scale: 1.08, 
                y: -10,
                rotateY: 5,
                transition: { type: "spring", stiffness: 400 }
              }}
              whileTap={{ scale: 0.95 }}
              onHoverStart={() => setHoveredLogo(index)}
              onHoverEnd={() => setHoveredLogo(null)}
              className={`${logo.bg} ${logo.textColor} aspect-square rounded-2xl shadow-lg cursor-pointer relative overflow-hidden group transition-shadow hover:shadow-2xl`}
              data-testid={`client-logo-${index}`}
            >
              {/* Animated background gradient */}
              <motion.div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: 'radial-gradient(circle at center, rgba(255,255,255,0.1) 0%, transparent 70%)'
                }}
                animate={{
                  scale: [1, 1.5, 1],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              
              {/* Logo Content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                <motion.span 
                  className={`${logo.style} text-center`}
                  animate={{
                    scale: hoveredLogo === index ? 1.15 : 1,
                  }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {logo.name}
                </motion.span>
              </div>

              {/* Hover Overlay */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: hoveredLogo === index ? 1 : 0,
                  y: hoveredLogo === index ? 0 : 20
                }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-6"
              >
                <motion.span 
                  className="text-white font-semibold mb-2"
                  initial={{ y: 10 }}
                  animate={{ y: hoveredLogo === index ? 0 : 10 }}
                >
                  {logo.name}
                </motion.span>
                <motion.span 
                  className="text-white/70 text-sm text-center"
                  initial={{ y: 10 }}
                  animate={{ y: hoveredLogo === index ? 0 : 10 }}
                  transition={{ delay: 0.05 }}
                >
                  {logo.description}
                </motion.span>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: hoveredLogo === index ? 1 : 0 }}
                  transition={{ delay: 0.1, type: "spring" }}
                >
                  <ExternalLink className="w-4 h-4 text-white/50 mt-3" />
                </motion.div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Featured Client */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center"
          data-testid="featured-client"
        >
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="inline-flex items-center gap-4 py-6 px-10 bg-white rounded-2xl shadow-lg border border-neutral-100"
          >
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <div className="text-left">
              <span className="font-serif text-xl md:text-2xl font-semibold text-black block">
                Selcius Restaurant
              </span>
              <span className="text-sm text-green-600">
                {language === 'fr' ? 'Partenaire Premium' : 'Premium Partner'}
              </span>
            </div>
          </motion.div>
        </motion.div>

        {/* Testimonial */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1 }}
          className="mt-12 max-w-2xl mx-auto text-center"
        >
          <blockquote className="text-lg md:text-xl text-neutral-600 italic">
            "{language === 'fr' 
              ? 'BAO Agency a transformé notre présence digitale. Leur expertise et leur créativité ont dépassé toutes nos attentes.'
              : 'BAO Agency transformed our digital presence. Their expertise and creativity exceeded all our expectations.'}"
          </blockquote>
          <div className="mt-4 text-sm text-neutral-500">
            — CEO, Selcius Restaurant
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BrandShowcase;
